/* eslint-disable react-refresh/only-export-components -- Router config: layout route components are not exported */
import React from "react";
import { createBrowserRouter, Outlet, useOutletContext } from "react-router";
import { getEventSubdomainFromHostname } from "@shared/utils/subdomain-url.utils";
import { AuthenticatedRouteGuard } from "@frontend/shared/presentation/routing/AuthenticatedRouteGuard";
import { LoginPage } from "@frontend/shared/presentation/pages/LoginPage";
import { MagicLinkPage } from "@frontend/shared/presentation/pages/MagicLinkPage";
import { OtpAuthPage } from "@frontend/shared/presentation/pages/OtpAuthPage";
import { UnauthorizedPage } from "@frontend/shared/presentation/pages/UnauthorizedPage";
import { DirectoryPage } from "./features/directory/presentation/pages/DirectoryPage";
import { ProfileDetailPage } from "./features/directory/presentation/pages/ProfileDetailPage";
import { ProfileManagementPage } from "./features/profile/presentation/pages/ProfileManagementPage";
import { ProfilePhotoPage } from "./features/profile/presentation/pages/ProfilePhotoPage";
import { UserAccountPage } from "./features/users/presentation/pages/UserAccountPage";
import { JoinEventRoute } from "./features/event-landing/presentation/pages/JoinEventRoute";
import { DefaultPage } from "./shared/presentation/pages/DefaultPage";
import {
  AnonymousEventRouteLayout,
  AuthenticatedEventRouteLayout,
} from "./shared/presentation/routing/EventRouteLayout";
import { RouteErrorBoundary } from "./shared/presentation/components/RouteErrorBoundary";

const eventSubdomain = getEventSubdomainFromHostname(window.location.hostname);

function RootLayout(): React.JSX.Element {
  return <Outlet context={{ eventSubdomain }} />;
}

function SessionLayout(): React.JSX.Element {
  const context = useOutletContext<{ eventSubdomain: string | null }>();
  return <Outlet context={context} />;
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "/auth",
        element: <OtpAuthPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "/m/:magicLinkIdPublic",
        element: <MagicLinkPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "/login",
        element: <LoginPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: "/unauthorized",
        element: <UnauthorizedPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        element: <SessionLayout />,
        errorElement: <RouteErrorBoundary />,
        children: [
          eventSubdomain !== null
            ? {
                path: "/",
                element: <AnonymousEventRouteLayout />,
                errorElement: <RouteErrorBoundary />,
                children: [{ index: true, element: <DefaultPage /> }],
              }
            : {
                path: "/",
                element: <DefaultPage />,
                errorElement: <RouteErrorBoundary />,
              },
          {
            path: "/e/:eventIdPublicOrSlug/join",
            element: <AnonymousEventRouteLayout />,
            errorElement: <RouteErrorBoundary />,
            children: [{ index: true, element: <JoinEventRoute /> }],
          },
          eventSubdomain === null
            ? {
                path: "/u/:userIdPublic",
                element: (
                  <AuthenticatedRouteGuard>
                    <UserAccountPage />
                  </AuthenticatedRouteGuard>
                ),
                errorElement: <RouteErrorBoundary />,
              }
            : {
                path: "/u/:userIdPublic",
                element: (
                  <AuthenticatedRouteGuard>
                    <AuthenticatedEventRouteLayout />
                  </AuthenticatedRouteGuard>
                ),
                errorElement: <RouteErrorBoundary />,
                children: [{ index: true, element: <UserAccountPage /> }],
              },
          {
            path: "/e/:eventIdPublicOrSlug",
            element: (
              <AuthenticatedRouteGuard>
                <AuthenticatedEventRouteLayout />
              </AuthenticatedRouteGuard>
            ),
            errorElement: <RouteErrorBoundary />,
            children: [
              {
                path: "d",
                element: <DirectoryPage />,
              },
              {
                path: "d/profile/:participantIdPublic",
                element: <ProfileDetailPage />,
              },
              {
                path: "p/me",
                element: <ProfileManagementPage />,
              },
              {
                path: "p/me/photo",
                element: <ProfilePhotoPage />,
              },
            ],
          },
          ...(eventSubdomain !== null
            ? [
                {
                  path: "/join",
                  element: <AnonymousEventRouteLayout />,
                  errorElement: <RouteErrorBoundary />,
                  children: [{ index: true, element: <JoinEventRoute /> }],
                },
                {
                  path: "/d",
                  element: (
                    <AuthenticatedRouteGuard>
                      <AuthenticatedEventRouteLayout />
                    </AuthenticatedRouteGuard>
                  ),
                  errorElement: <RouteErrorBoundary />,
                  children: [
                    {
                      index: true,
                      element: <DirectoryPage />,
                    },
                  ],
                },
                {
                  path: "/d/profile/:participantIdPublic",
                  element: (
                    <AuthenticatedRouteGuard>
                      <AuthenticatedEventRouteLayout />
                    </AuthenticatedRouteGuard>
                  ),
                  errorElement: <RouteErrorBoundary />,
                  children: [
                    {
                      index: true,
                      element: <ProfileDetailPage />,
                    },
                  ],
                },
                {
                  path: "/p/me",
                  element: (
                    <AuthenticatedRouteGuard>
                      <AuthenticatedEventRouteLayout />
                    </AuthenticatedRouteGuard>
                  ),
                  errorElement: <RouteErrorBoundary />,
                  children: [
                    {
                      index: true,
                      element: <ProfileManagementPage />,
                    },
                    {
                      path: "photo",
                      element: <ProfilePhotoPage />,
                    },
                  ],
                },
              ]
            : []),
        ],
      },
    ],
  },
]);
